'use client'

/**
 * LocatiePicker — smart, zoekbare locatie-combobox (spec NN §3.2.1B).
 *
 * Eén dropdown over álle bestaande locatie-bronnen in het systeem (eigen
 * BV-vestigingen, klantlocaties, opleider-locaties) plus de opties "Online
 * (Teams/Zoom)" en "Nieuw adres via Google Places…". Bovenop {@link Combobox}
 * (cmdk + Radix) zodat toetsenbordnavigatie, a11y en het zoekfilter over alle
 * groepen heen al geregeld zijn.
 *
 * Framework-agnostisch: géén Next, géén apiFetch, géén Google-Places-import.
 * De data komt via `sources` (door de backoffice server-side opgehaald) en het
 * Places-adresveld wordt via de `renderPlacesInput`-render-prop geïnjecteerd.
 */

import * as React from 'react'
import { MapPin, Pencil } from 'lucide-react'
import { Combobox } from './combobox'
import {
  buildLocatieOptions,
  decodeLocatieOptionValue,
  locatieValueToOptionValue,
  locatieValueToTriggerLabel,
  type LocatiePickerSources,
  type LocatiePickerType,
  type LocatiePickerValue,
} from '../lib/locatie-picker-options'

export type {
  LocatiePickerSources,
  LocatiePickerType,
  LocatiePickerValue,
} from '../lib/locatie-picker-options'

export interface LocatiePickerProps {
  value: LocatiePickerValue | null
  onChange: (v: LocatiePickerValue | null) => void
  sources: LocatiePickerSources
  /** Toon de "Online (Teams/Zoom)"-optie. Default true. */
  allowOnline?: boolean
  /** Toon de "Nieuw adres via Google Places…"-optie. Default true. */
  allowCustom?: boolean
  /**
   * Beperk de keuzes tot deze types. Eén type + een waarde → effectief
   * read-only (TRAINING-events locken op de opleider-locatie van de sessie).
   */
  lockedTypes?: LocatiePickerType[]
  /**
   * Render-prop voor het Places-adresveld (geïnjecteerd door de backoffice,
   * die Google Places kent). Roept `onPick` aan met het geselecteerde adres.
   * Zonder deze prop wordt de custom-optie verborgen (geen kapotte staat).
   */
  renderPlacesInput?: (args: {
    onPick: (custom: {
      adres: string
      lat: number | null
      lng: number | null
      placeId?: string | null
    }) => void
  }) => React.ReactNode
  /** Serverside zoeken op klantlocaties (doorgegeven aan Combobox.onSearchChange). */
  onClientLocationSearch?: (query: string) => void
  disabled?: boolean
  id?: string
  placeholder?: string
}

export function LocatiePicker({
  value,
  onChange,
  sources,
  allowOnline = true,
  allowCustom = true,
  lockedTypes,
  renderPlacesInput,
  onClientLocationSearch,
  disabled = false,
  id,
  placeholder = 'Kies locatie…',
}: LocatiePickerProps) {
  // Custom-optie alleen tonen als er ook daadwerkelijk een Places-veld is om
  // op terug te vallen — anders zou de gebruiker in een dood pad belanden.
  const effectiveAllowCustom = allowCustom && !!renderPlacesInput

  // "Custom-invoermodus": de gebruiker koos "Nieuw adres via Google Places…"
  // en moet nu het adres invoeren via het geïnjecteerde Places-veld.
  const [customMode, setCustomMode] = React.useState(false)

  const options = React.useMemo(
    () =>
      buildLocatieOptions(sources, {
        allowOnline,
        allowCustom: effectiveAllowCustom,
        lockedTypes,
      }),
    [sources, allowOnline, effectiveAllowCustom, lockedTypes],
  )

  // Effectief read-only: precies één toegestaan type én er is al een waarde
  // gekozen. Dan tonen we de vaste locatie zonder verdere keuzemogelijkheid
  // (spec: TRAINING-events locken op OPLEIDER_LOCATIE vanuit de sessie).
  const isLocked = !!lockedTypes && lockedTypes.length === 1
  const lockedReadOnly = isLocked && value !== null

  const handleComboChange = (encoded: string | null) => {
    if (encoded === null) {
      onChange(null)
      return
    }
    const decoded = decodeLocatieOptionValue(encoded, sources)
    if (decoded === null) return
    if ('sentinel' in decoded) {
      // CUSTOM-sentinel → schakel naar Places-invoermodus.
      setCustomMode(true)
      return
    }
    setCustomMode(false)
    onChange(decoded)
  }

  const handlePlacesPick = (custom: {
    adres: string
    lat: number | null
    lng: number | null
    placeId?: string | null
  }) => {
    onChange({
      type: 'CUSTOM',
      adres: custom.adres,
      lat: custom.lat,
      lng: custom.lng,
      placeId: custom.placeId ?? null,
    })
    setCustomMode(false)
  }

  // ── Read-only lock: toon de vaste locatie, geen combobox ──────────────────
  if (lockedReadOnly) {
    return (
      <div
        id={id}
        className="flex w-full items-center gap-2 rounded-md border border-[#CED0C8] bg-[#F0F1EC] px-2 py-1.5 text-sm text-[#1A1A18]"
      >
        <MapPin className="h-3.5 w-3.5 shrink-0 text-[#5C5C58]" aria-hidden="true" />
        <span className="flex-1">{locatieValueToTriggerLabel(value)}</span>
      </div>
    )
  }

  // ── Custom-invoermodus: Places-veld + "andere locatie kiezen"-uitweg ──────
  const inCustomMode = customMode || (value?.type === 'CUSTOM' && !!renderPlacesInput)
  if (inCustomMode && renderPlacesInput) {
    return (
      <div className="space-y-1.5">
        {renderPlacesInput({ onPick: handlePlacesPick })}
        {value?.type === 'CUSTOM' && (
          <p className="px-0.5 text-xs text-[#5C5C58]">
            Huidig adres: <span className="text-[#1A1A18]">{value.adres}</span>
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            setCustomMode(false)
            if (value?.type === 'CUSTOM') onChange(null)
          }}
          className="inline-flex items-center gap-1 rounded text-xs font-medium text-[#5C5C58] hover:text-[#1A1A18]"
        >
          <Pencil className="h-3 w-3" aria-hidden="true" />
          Andere locatie kiezen
        </button>
      </div>
    )
  }

  return (
    <Combobox
      id={id}
      options={options}
      value={locatieValueToOptionValue(value)}
      onChange={handleComboChange}
      onSearchChange={onClientLocationSearch}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder="Zoek vestiging, klant, opleider…"
      emptyText="Geen locaties gevonden"
    />
  )
}
